import { createApp } from 'vue'
// Vuetify
import 'vuetify/styles'
// Public site design (scoped under .tdi); loaded after Vuetify so it wins ties.
import './styles/site.css'
import './styles/app.css'
import { createVuetify } from 'vuetify'
import { VDivider } from 'vuetify/components/VDivider'
import { VTextField } from 'vuetify/components/VTextField'

import App from './App.vue'
import router from './components/router'
import store from './store'
import { auth } from './firebase.config'
import { onIdTokenChanged, signOut } from 'firebase/auth'
import { isSsoEnabled, restoreSsoSession, SsoError } from './domain/sharedSso'

let app

const vuetify = createVuetify({
    components: { VDivider, VTextField },
})

function sendErrorToServer(payload) {
    const endpoint =
        process.env.VUE_APP_API_BASE_URL
            ? `${process.env.VUE_APP_API_BASE_URL}/api/frontend-error`
            : '/api/frontend-error'

    fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            ...payload,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            uid: auth.currentUser?.uid || null,
        }),
    }).catch(() => {
        // 전송 실패는 무시
    })
}

function setupVueErrorHandler(vueApp) {
    vueApp.config.errorHandler = (err, instance, info) => {
        console.error('Vue Error:', err)

        sendErrorToServer({
            type: 'vue',
            message: err?.message || String(err),
            stack: err?.stack || null,
            info,
            component:
                instance?.$?.type?.name ||
                instance?.$options?.name ||
                instance?.$options?.__name ||
                null,
        })
    }
}

function setupGlobalErrorHandlers() {
    window.addEventListener('error', (event) => {
        sendErrorToServer({
            type: 'window_error',
            message: event.message || 'Unknown window error',
            source: event.filename || null,
            line: event.lineno || null,
            column: event.colno || null,
            stack: event.error?.stack || null,
        })
    })

    window.addEventListener('unhandledrejection', (event) => {
        sendErrorToServer({
            type: 'unhandledrejection',
            message: event.reason?.message || String(event.reason),
            stack: event.reason?.stack || null,
        })
    })
}

setupGlobalErrorHandlers()

// Main host owns the shared session. Keep its Firebase state aligned without
// redirecting visitors away from the public website.
if (isSsoEnabled()) {
    onIdTokenChanged(auth, (user) => {
        if (!user) store.commit('authStore/clearUser')
    })
    let ssoBusy = false
    const checkSso = async () => {
        if (ssoBusy || document.visibilityState === 'hidden' || window.location.pathname === '/sso') return
        ssoBusy = true
        try {
            await auth.authStateReady()
            await restoreSsoSession(auth)
            if (auth.currentUser) store.commit('authStore/setUser', auth.currentUser)
        } catch (error) {
            if (error instanceof SsoError && error.status === 403) await signOut(auth)
            else if (error instanceof SsoError && error.status === 401 && auth.currentUser) {
                // Detect global revocation, while preserving old local sessions
                // until a fresh login establishes their first central session.
                try { await auth.currentUser.getIdToken(true) } catch { await signOut(auth) }
            }
        } finally { ssoBusy = false }
    }
    setInterval(() => { void checkSso() }, 60000)
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void checkSso() })
    void checkSso()
}

const unsubscribe = auth.onAuthStateChanged(() => {
    if (!app) {
        const vueApp = createApp(App)

        setupVueErrorHandler(vueApp)

        app = vueApp
            .use(store)
            .use(router)
            .use(vuetify)
            .mount('#app')
    }

    unsubscribe()
})
